import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Button,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel,
    Typography,
    IconButton,
    Box,
    Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import Loader from '../../common/components/Loader';
import { useGlobalStore } from '../../application/store/store';
import { NavigationRoutes } from '../../application/router/routes';
import { MessageListDto } from './services/messagesService';
import DeleteMessageDialog from './components/DeleteMessageDialog';

type Order = 'asc' | 'desc';
type OrderBy = 'title' | 'createdByName' | 'createdOn' | 'readCount';

const MessagesPage = () => {
    const navigate = useNavigate();
    const {
        messages,
        messagesLoading,
        messagesError,
        fetchMessages,
        deleteMessage,
        clearMessagesError,
    } = useGlobalStore();

    const [order, setOrder] = useState<Order>('desc');
    const [orderBy, setOrderBy] = useState<OrderBy>('createdOn');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [messageToDelete, setMessageToDelete] =
        useState<MessageListDto | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);

    useEffect(() => {
        fetchMessages();
    }, []);

    const handleRequestSort = (property: OrderBy) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const sortedMessages = [...messages].sort((a, b) => {
        let comparison = 0;
        switch (orderBy) {
            case 'title':
                comparison = a.title.localeCompare(b.title);
                break;
            case 'createdByName':
                comparison = a.createdByName.localeCompare(b.createdByName);
                break;
            case 'createdOn':
                comparison =
                    new Date(a.createdOn).getTime() -
                    new Date(b.createdOn).getTime();
                break;
            case 'readCount':
                comparison =
                    a.readCount / a.totalUserCount -
                    b.readCount / b.totalUserCount;
                break;
        }
        return order === 'asc' ? comparison : -comparison;
    });

    const paginatedMessages = sortedMessages.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
    );

    const handleChangePage = (_event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleDeleteClick = (
        e: React.MouseEvent,
        message: MessageListDto
    ) => {
        e.stopPropagation();
        setMessageToDelete(message);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (!messageToDelete) return;

        setIsDeleting(true);
        try {
            await deleteMessage(messageToDelete.id);
            setDeleteDialogOpen(false);
            setMessageToDelete(null);
        } catch (error) {
            console.error('Error deleting message:', error);
        } finally {
            setIsDeleting(false);
        }
    };

    const handleDeleteCancel = () => {
        setDeleteDialogOpen(false);
        setMessageToDelete(null);
    };

    const handleRowClick = (messageId: string) => {
        navigate(`${NavigationRoutes.messages}/${messageId}`);
    };

    if (messagesLoading && messages.length === 0) {
        return <Loader />;
    }

    return (
        <div className="p-3">
            <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={3}
            >
                <Typography variant="h4" fontWeight="bold">
                    Meddelanden
                </Typography>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={() => navigate(NavigationRoutes.createMessage)}
                >
                    Nytt meddelande
                </Button>
            </Box>

            {messagesError && (
                <Alert
                    severity="error"
                    onClose={clearMessagesError}
                    sx={{ mb: 2 }}
                >
                    {messagesError}
                </Alert>
            )}

            {messages.length === 0 ? (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                    <Typography
                        variant="h6"
                        color="text.secondary"
                        gutterBottom
                    >
                        Inga meddelanden
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Du har inte skickat några meddelanden ännu
                    </Typography>
                </Paper>
            ) : (
                <>
                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead
                                sx={{
                                    '& .MuiTableSortLabel-root.Mui-active': {
                                        color: 'inherit',
                                    },
                                    '& .MuiTableSortLabel-icon': {
                                        color: 'inherit !important',
                                    },
                                }}
                            >
                                <TableRow>
                                    <TableCell>
                                        <TableSortLabel
                                            active={orderBy === 'title'}
                                            direction={
                                                orderBy === 'title'
                                                    ? order
                                                    : 'asc'
                                            }
                                            onClick={() =>
                                                handleRequestSort('title')
                                            }
                                        >
                                            Titel
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell>
                                        <TableSortLabel
                                            active={orderBy === 'createdByName'}
                                            direction={
                                                orderBy === 'createdByName'
                                                    ? order
                                                    : 'asc'
                                            }
                                            onClick={() =>
                                                handleRequestSort(
                                                    'createdByName'
                                                )
                                            }
                                        >
                                            Avsändare
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell>
                                        <TableSortLabel
                                            active={orderBy === 'createdOn'}
                                            direction={
                                                orderBy === 'createdOn'
                                                    ? order
                                                    : 'asc'
                                            }
                                            onClick={() =>
                                                handleRequestSort('createdOn')
                                            }
                                        >
                                            Datum
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell>
                                        <TableSortLabel
                                            active={orderBy === 'readCount'}
                                            direction={
                                                orderBy === 'readCount'
                                                    ? order
                                                    : 'asc'
                                            }
                                            onClick={() =>
                                                handleRequestSort('readCount')
                                            }
                                        >
                                            Lästa
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell align="right">
                                        Åtgärder
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {paginatedMessages.map((message) => (
                                    <TableRow
                                        key={message.id}
                                        hover
                                        sx={{ cursor: 'pointer' }}
                                        onClick={() =>
                                            handleRowClick(message.id)
                                        }
                                    >
                                        <TableCell>{message.title}</TableCell>
                                        <TableCell>
                                            {message.createdByName}
                                        </TableCell>
                                        <TableCell>
                                            {new Date(
                                                message.createdOn
                                            ).toLocaleDateString('sv-SE')}
                                        </TableCell>
                                        <TableCell>
                                            {message.readCount}/
                                            {message.totalUserCount}
                                        </TableCell>
                                        <TableCell align="right">
                                            <IconButton
                                                size="small"
                                                color="error"
                                                onClick={(e) =>
                                                    handleDeleteClick(
                                                        e,
                                                        message
                                                    )
                                                }
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        component="div"
                        count={messages.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        rowsPerPageOptions={[5, 10, 25]}
                        labelRowsPerPage="Antal poster:"
                        labelDisplayedRows={({ from, to, count }) =>
                            `${from}-${to} av ${count !== -1 ? count : `mer än ${to}`}`
                        }
                    />
                </>
            )}

            <DeleteMessageDialog
                open={deleteDialogOpen}
                message={messageToDelete}
                onConfirm={handleDeleteConfirm}
                onCancel={handleDeleteCancel}
                isDeleting={isDeleting}
            />
        </div>
    );
};

export default MessagesPage;
